// Group the inventory's protocol twins under one lead row per PHYSICAL
// instrument (task lean_device_sync / sk_rows_unfolded — the presentation
// half of the owner's decision).
//
// The DATA never merges: the July `sk_device_duplicates` decision (option B)
// keeps one row per protocol view, sharing `busIdentityFingerprint` as a
// read-only cross-reference, and this module honours that — it only decides
// which row LEADS a visual group and which rows nest beneath it as quiet
// aliases. Rows without a fingerprint, and sole holders of one, lead
// themselves; nothing is ever hidden from the DOM's reach (every twin stays
// a link to its own device page).

import { deviceChannelRows } from './deviceChannels';
import type { VesselDevice } from './useBoatDevices';

export interface GroupedInventory {
  /** One row per physical instrument — what the sections render as cards. */
  leads: VesselDevice[];
  /** Lead `deviceId` → its other protocol views, deterministic order. */
  twinsByLead: Map<string, VesselDevice[]>;
}

function fingerprintOf(device: VesselDevice): string | null {
  const raw: unknown = device.busIdentityFingerprint;
  if (typeof raw !== 'string' || raw === '') return null;
  return raw;
}

/**
 * Which of two same-fingerprint rows should lead the group. Native N2K first
 * (it is the protocol the identity is measured on, and the July task showed
 * it is the side that carries the label and the channels); then the row that
 * reports more channels; then a row with an operator/device label; then the
 * lexicographically smallest `deviceId` — the same determinism rule
 * `findRicherTwin` uses, so the group never flips between refetches.
 */
function leadRank(device: VesselDevice): [number, number, number, string] {
  return [
    device.protocol === 'n2k' ? 0 : 1,
    -deviceChannelRows(device).length,
    device.deviceLabel ? 0 : 1,
    device.deviceId,
  ];
}

function compareLead(a: VesselDevice, b: VesselDevice): number {
  const [aProto, aChannels, aLabel, aId] = leadRank(a);
  const [bProto, bChannels, bLabel, bId] = leadRank(b);
  return aProto - bProto || aChannels - bChannels || aLabel - bLabel || aId.localeCompare(bId);
}

/**
 * Group whatever list the caller already filtered. Applied AFTER filtering on
 * purpose: a filter that matches only the twin (say, the Signal K gateway
 * filter) leaves that twin standing alone as its own honest row rather than
 * silently vanishing under a lead the filter excluded.
 */
export function groupProtocolTwins(devices: readonly VesselDevice[]): GroupedInventory {
  const byFingerprint = new Map<string, VesselDevice[]>();
  for (const device of devices) {
    const fp = fingerprintOf(device);
    if (fp === null) continue;
    const bucket = byFingerprint.get(fp);
    if (bucket) bucket.push(device);
    else byFingerprint.set(fp, [device]);
  }

  const nested = new Set<string>();
  const twinsByLead = new Map<string, VesselDevice[]>();
  for (const bucket of byFingerprint.values()) {
    if (bucket.length < 2) continue;
    const [lead, ...twins] = [...bucket].sort(compareLead);
    if (lead === undefined) continue;
    for (const twin of twins) nested.add(twin.deviceId);
    twinsByLead.set(lead.deviceId, twins);
  }

  return {
    leads: devices.filter((device) => !nested.has(device.deviceId)),
    twinsByLead,
  };
}
